/**
 * Species API - Cliente HTTP para el catálogo global de especies arbóreas
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/map-tools
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * Especie del catálogo global
 */
export interface Species {
  id: string;
  commonName: string;
  scientificName: string;
  botanicalFamily: string | null;
  origin: 'native' | 'exotic' | null;
  isActive: boolean;
  createdAt: string;
}

/**
 * DTO para registrar una nueva especie
 */
export interface CreateSpeciesDto {
  commonName: string;
  scientificName: string;
  botanicalFamily?: string;
  origin?: 'native' | 'exotic';
}

/**
 * Cliente API para el catálogo de especies
 */
export const speciesApi = {
  /**
   * Obtiene todas las especies activas del catálogo, ordenadas por nombre común
   */
  async getAll(): Promise<Species[]> {
    const token = getAuthToken();
    const response = await axios.get<Species[]>(`${API_URL}/species`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Agrega una nueva especie al catálogo global
   *
   * @param dto - Datos de la especie (nombre común + científico obligatorios)
   * @returns Especie creada
   */
  async create(dto: CreateSpeciesDto): Promise<Species> {
    const token = getAuthToken();
    const response = await axios.post<Species>(`${API_URL}/species`, dto, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};
